// Every flat, short-lived shape that isn't a particle: muzzle stars, flashes, tracers, ground
// rings and markers, ray bursts, shield hex flicker, lightning arcs, jet flames. One ring
// buffer of quads, one draw; each card picks its shape (mode) and how it faces (orient).

import * as THREE from 'three';
import { Ring, quadGeometry, hazeUniforms, HAZE_VS, HASH_GLSL, ATLAS_GLSL, premulMaterial, fxAtlas } from './gpu';
import type { RGB } from './particles';

export const enum CMode {
  Flash = 0, Shock = 1, Marker = 2, Rays = 3, Hex = 4, Quake = 5, Streak = 6, Arc = 7, Flame = 8, Muzzle = 9, Diamond = 10, Glow = 11,
  /** ground decal from the atlas: p0 = style (0 scorch 1 acid 2 crackle 3 frost 4 water 5 steam-wet) */
  Decal = 12,
  /** soft atlas glow, additive (fake bloom) */
  Soft = 13,
  /** atlas star flare, additive; p0 = 0 eight spikes, 1 four, 2 six */
  Flare = 14,
  /** Gate vortex (orient Plane): rotating log-spiral bands, ink between them, a gold rim. alpha = charge; life envelope sin^2 so two cards half a life apart sum flat. */
  Vortex = 15,
}
export const enum COrient { Billboard = 0, Ground = 1, Segment = 2, Axis = 3, /** a quad in the plane whose normal is the aux vector */ Plane = 4 }
export const enum CFlag { Add = 1, NoHaze = 8 }

const STRIDE = 24;

const VS = /* glsl */ `
${HAZE_VS}
attribute vec4 aA; // pos, t0
attribute vec4 aB; // aux (end point or direction), life
attribute vec4 aC; // size0, size1, mode, orient
attribute vec4 aD; // col rgba
attribute vec4 aE; // col2 rgb, flags
attribute vec4 aP; // params
uniform float uTime;
varying vec2 vUv;
varying vec4 vCol;
varying vec3 vCol2;
varying float vMode;
varying float vAdd;
varying float vLife;
varying float vAge;
varying float vR;
varying vec4 vP;
varying float vHaze;
varying vec3 vHazeCol;
void main() {
  float age = uTime - aA.w;
  float life = aB.w;
  if ( age < 0.0 || age > life ) { gl_Position = vec4( 2.0, 2.0, 2.0, 1.0 ); return; }
  float lt = age / life;
  float ease = 1.0 - pow( 1.0 - lt, 3.0 );
  int mode = int( aC.z + 0.5 );
  int orient = int( aC.w + 0.5 );
  vec2 q = position.xy;
  vec3 wp;
  float ext = mix( aC.x, aC.y, ease );
  vR = 1.0;
  if ( mode == 1 || mode == 5 || mode == 3 || mode == 10 ) { ext = aC.y; vR = mix( aC.x, aC.y, ease ) / max( aC.y, 1e-3 ); }
  if ( mode == 2 ) { ext = aC.x; }
  vUv = q * 2.0;
  float near = 1.0;
  if ( orient == 0 ) {
    // near-camera fade + screen-size cap (see particles)
    float camD = length( aA.xyz - cameraPosition );
    if ( camD < 1.5 ) { gl_Position = vec4( 2.0, 2.0, 2.0, 1.0 ); return; }
    ext = min( ext, camD * 0.28 );
    near = smoothstep( 1.5, 4.0, camD );
    vec3 right = vec3( viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0] );
    vec3 up = vec3( viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1] );
    float rot = aP.w;
    float c = cos( rot ), s = sin( rot );
    vec2 r2 = vec2( c * q.x - s * q.y, s * q.x + c * q.y );
    wp = aA.xyz + ( right * r2.x + up * r2.y ) * ext * 2.0;
  } else if ( orient == 1 ) {
    float rot = aP.w;
    float c = cos( rot ), s = sin( rot );
    vec2 r2 = vec2( c * q.x - s * q.y, s * q.x + c * q.y );
    wp = aA.xyz + vec3( r2.x, 0.0, r2.y ) * ext * 2.0 + vec3( 0.0, mode == 12 ? 0.05 : 0.07, 0.0 );
  } else if ( orient == 4 ) {
    float nl = length( aB.xyz );
    vec3 n = nl > 1e-5 ? aB.xyz / nl : vec3( 0.0, 0.0, 1.0 );
    vec3 t = cross( vec3( 0.0, 1.0, 0.0 ), n );
    float tl = length( t );
    t = tl > 1e-4 ? t / tl : vec3( 1.0, 0.0, 0.0 );
    vec3 u = cross( n, t );
    float rot = aP.w;
    float c = cos( rot ), s = sin( rot );
    vec2 r2 = vec2( c * q.x - s * q.y, s * q.x + c * q.y );
    wp = aA.xyz + ( t * r2.x + u * r2.y ) * ext * 2.0;
    near = smoothstep( 1.5, 4.0, length( aA.xyz - cameraPosition ) );
  } else {
    vec3 A = aA.xyz, B;
    float W;
    if ( orient == 2 ) {
      B = aB.xyz; W = aC.x;
      if ( mode == 6 ) {
        W *= 3.0; // the glow sheath around the hot core
        // tracer: a segment that runs from muzzle to impact
        float head = min( age / max( life * 0.72, 1e-3 ), 1.0 );
        float tail = max( head - aP.x, 0.0 ) + max( lt - 0.72, 0.0 ) / 0.28 * ( 1.0 - max( head - aP.x, 0.0 ) );
        vec3 A0 = A;
        A = mix( A0, B, min( tail, head ) ); B = mix( A0, B, head );
      }
    } else {
      // a zero direction (the game can send one for a muzzle) must not normalize to NaN: one NaN
      // pixel poisons the HDR bloom mips into checkerboard blocks
      float al = length( aB.xyz );
      B = A + ( al > 1e-5 ? aB.xyz / al : vec3( 0.0, 1.0, 0.0 ) ) * aC.x; W = aC.y;
    }
    vec3 ax = B - A;
    vec3 mid = A + ax * ( q.y + 0.5 );
    vec3 side = cross( ax, cameraPosition - mid );
    float sl = length( side );
    side = sl > 1e-5 ? side / sl : vec3( 1.0, 0.0, 0.0 );
    wp = mid + side * q.x * W;
    vUv = vec2( q.x * 2.0, q.y + 0.5 );
  }
  if ( orient >= 2 ) near = smoothstep( 1.0, 3.0, length( wp - cameraPosition ) ); // segments fade per vertex
  gl_Position = projectionMatrix * viewMatrix * vec4( wp, 1.0 );
  vCol = aD; vCol.a *= near; vCol2 = aE.rgb; vAdd = mod( aE.w, 2.0 );
  vMode = aC.z; vLife = lt; vAge = age; vP = aP;
  vHaze = mod( floor( aE.w / 8.0 ), 2.0 ) > 0.5 ? 0.0 : hazeAt( aA.xyz, vHazeCol );
}
`;

const FS = /* glsl */ `
${HASH_GLSL}
${ATLAS_GLSL}
varying vec2 vUv;
varying vec4 vCol;
varying vec3 vCol2;
varying float vMode;
varying float vAdd;
varying float vLife;
varying float vAge;
varying float vR;
varying vec4 vP;
varying float vHaze;
varying vec3 vHazeCol;
const float TAU = 6.2831853;
uniform float uTime;
void main() {
  vec2 p = vUv;
  float r = length( p );
  float th = atan( p.y, p.x );
  int mode = int( vMode + 0.5 );
  float a = 0.0;
  vec3 col = vCol.rgb;
  float fade = 1.0;
  if ( mode == 0 ) {
    // star flash: N hard spikes around a hot core
    float k = th / TAU * vP.x + vP.y;
    float d = abs( fract( k ) - 0.5 ) * 2.0;
    float spike = mix( 0.42, 1.0, pow( 1.0 - d, 2.5 ) );
    a = step( r, spike * 0.95 );
    col = mix( vCol.rgb, vCol2, smoothstep( 0.25, 0.6, r ) );
    // alpha-blended hit cards get a printed ink edge, so they pop on the bright meadow
    if ( vAdd < 0.5 && r > spike * 0.95 - 0.13 ) col = vec3( 0.06, 0.03, 0.02 );
    fade = 1.0 - vLife * vLife;
  } else if ( mode == 1 ) {
    // shockwave ring: two-tone band
    float w = vP.x;
    float rr = vR;
    a = step( rr - w, r ) * step( r, rr );
    col = r > rr - w * 0.45 ? vCol.rgb : vCol2;
    fade = 1.0 - smoothstep( 0.35, 1.0, vLife );
  } else if ( mode == 2 ) {
    // landing / arrival marker: outer ring + a disc filling toward the moment it lands
    float fill = clamp( vAge / max( vP.x, 1e-3 ), 0.0, 1.0 );
    float ring = step( 0.86, r ) * step( r, 0.98 );
    float ink = step( 0.8, r ) * step( r, 0.86 );
    float disc = step( r, fill * 0.8 );
    float edge = step( fill * 0.8 - 0.07, r ) * disc;
    float ticks = step( 0.93, abs( cos( th * 4.0 ) ) ) * step( 0.6, r ) * step( r, 0.8 );
    float pulse = 0.75 + 0.25 * sin( vAge * ( 10.0 + fill * 18.0 ) );
    a = max( max( ring, ink ), max( disc * ( 0.32 + 0.3 * fill ), max( edge, ticks * 0.7 ) ) );
    col = ink > 0.5 ? vec3( 0.04, 0.035, 0.015 ) : ( ring > 0.5 || edge > 0.5 ? vCol.rgb * pulse : vCol2 );
    float endT = vP.x;
    fade = 1.0 - smoothstep( 0.0, 0.18, vAge - endT );
    if ( vAge > endT ) col = mix( col, vec3( 1.0 ), 0.5 );
  } else if ( mode == 3 ) {
    // ray burst: tapering hard rays of uneven length
    float k = th / TAU * vP.x + vP.y;
    float idx = floor( k );
    float len = vR * mix( 0.55, 1.0, h11( idx + vP.y * 7.0 ) );
    float d = abs( fract( k ) - 0.5 ) * 2.0;
    a = step( d, 0.42 * ( 1.0 - r / len ) ) * step( 0.12, r );
    col = mix( vCol.rgb, vCol2, r / len );
    fade = 1.0 - smoothstep( 0.4, 1.0, vLife );
  } else if ( mode == 4 ) {
    // shield: a hex lattice flickering out from the hit
    vec2 h = p * 3.2;
    vec2 g = vec2( h.x * 1.1547, h.y + h.x * 0.57735 );
    vec2 cell = floor( g );
    vec2 f = fract( g );
    float e = min( min( f.x, f.y ), min( 1.0 - f.x, 1.0 - f.y ) );
    float on = step( 0.45, h21( cell + floor( vAge * 28.0 ) ) );
    a = step( e, 0.12 ) * step( r, 1.0 - vLife * 0.5 ) + on * 0.35 * step( r, 0.6 );
    a *= 1.0 - r;
    a = min( a * 1.6, 1.0 );
    fade = 1.0 - vLife;
  } else if ( mode == 5 ) {
    // quake: cracked ground ring with radial fractures
    float rr = vR;
    float ring = step( rr - 0.07, r ) * step( r, rr );
    float n = vnoise( vec2( th * 7.0, vP.y ) );
    float jag = vnoise( vec2( r * 18.0, th * 3.0 + vP.y ) ) * 0.06;
    float crack = step( 0.9, abs( sin( th * vP.x + vP.y + jag * 20.0 ) ) ) * step( 0.75, n + r * 0.2 ) * step( r, rr * 0.98 ) * step( 0.12, r );
    a = max( ring, crack );
    col = ring > 0.5 ? vCol.rgb : vCol2;
    fade = 1.0 - smoothstep( 0.45, 1.0, vLife );
  } else if ( mode == 6 ) {
    // tracer: a hot tapering core inside a soft glow sheath (x is in core half-widths)
    float y = clamp( p.y, 0.0, 1.0 );
    float w = mix( 0.3, 1.0, y );
    float x = abs( p.x ) * 3.0;
    float coreA = 1.0 - smoothstep( w * 0.8, w, x );
    float glow = exp( -x * x * 0.45 / ( w * w ) ) * 0.45 * y;
    a = max( coreA, glow );
    col = mix( vCol2, vCol.rgb, y ) * ( coreA + glow * 0.8 ) / max( a, 1e-3 ) + ( 1.0 - smoothstep( 0.0, w * 0.45, x ) ) * vec3( 0.5, 0.45, 0.35 ) * y;
  } else if ( mode == 7 ) {
    // lightning arc
    float y = p.y;
    float flick = floor( vAge * 26.0 );
    float env = sin( 3.14159 * clamp( y, 0.0, 1.0 ) );
    float off = ( vnoise( vec2( y * 7.0, vP.y * 13.0 + flick ) ) - 0.5 ) * 1.5 * env;
    off += ( vnoise( vec2( y * 23.0, vP.y * 5.0 + flick * 1.7 ) ) - 0.5 ) * 0.5 * env;
    float d = abs( p.x - off );
    float off2 = off + ( vnoise( vec2( y * 11.0, vP.y + flick ) ) - 0.5 ) * 0.9 * env;
    float d2 = abs( p.x - off2 );
    float bolt = max( 1.0 - smoothstep( 0.16, 0.22, d ), ( 1.0 - smoothstep( 0.06, 0.09, d2 ) ) * step( 0.4, y ) );
    float glow = exp( -d * d * 10.0 ) * 0.55 * env;
    a = max( bolt, glow );
    col = ( mix( vCol.rgb, vec3( 1.2, 1.15, 1.2 ), 1.0 - smoothstep( 0.04, 0.09, d ) ) * bolt + vCol2 * glow * 1.4 ) / max( a, 1e-3 );
    fade = 1.0 - smoothstep( 0.6, 1.0, vLife );
  } else if ( mode == 8 ) {
    // jet flame: teardrop with a white-hot core, flickering length
    float y = p.y;
    float fl = 0.85 + 0.15 * vnoise( vec2( vAge * 40.0, vP.y * 9.0 ) );
    float w = pow( max( 1.0 - y / fl, 0.0 ), 0.7 ) * smoothstep( -0.05, 0.12, y );
    a = step( abs( p.x ), w );
    col = mix( vCol2, vCol.rgb, step( abs( p.x ), w * 0.45 ) );
    fade = 1.0 - vLife * 0.5;
  } else if ( mode == 9 ) {
    // side muzzle flash: a jagged cone
    float y = p.y;
    float notch = 0.75 + 0.25 * step( 0.5, fract( y * 3.0 + vP.y ) );
    float w = ( 1.0 - y ) * notch * smoothstep( 0.0, 0.08, y );
    a = step( abs( p.x ), w );
    col = mix( vCol.rgb, vCol2, y );
    fade = 1.0 - vLife;
  } else if ( mode == 10 ) {
    // prime pop: diamond outline expanding, filled diamond flash at the start
    float d = abs( p.x ) + abs( p.y );
    float rr = vR;
    a = max( step( rr - 0.16, d ) * step( d, rr ), step( d, 0.55 * ( 1.0 - vLife * 2.5 ) ) );
    col = d < rr - 0.16 ? vCol2 : vCol.rgb;
    fade = 1.0 - smoothstep( 0.5, 1.0, vLife );
  } else if ( mode == 12 ) {
    // ground decal (hazard footprints, scorch marks)
    int st = int( vP.x + 0.5 );
    float fin = smoothstep( 0.0, 0.12, vAge );
    fade = fin * ( 1.0 - smoothstep( 0.8, 1.0, vLife ) );
    float emis = 0.0; vec3 ec = vCol2;
    if ( st == 0 ) {
      // scorch: soot with radial streaks, embers that cool off
      vec3 t = tile( 11.0, p );
      a = clamp( t.r * 0.85 + t.g * 0.3, 0.0, 1.0 );
      col = vCol.rgb * ( 0.75 + 0.5 * t.g );
      emis = t.b * ( 1.0 - smoothstep( 0.0, 0.5, vLife ) ) * 2.0;
    } else if ( st == 1 ) {
      // acid pool: a chartreuse sheet that SIMMERS (two drifting noise layers -> a moving sheen and
      // cells that swell into bubbles and pop), a thick printed ink rim, foam
      vec3 t = tile( 13.0, p );
      vec3 t2 = tile( 13.0, vec2( -p.y, p.x ) * 0.9 );
      vec2 sp = p * 5.0 + vP.y;
      float n1 = vnoise( sp + vec2( vAge * 0.35, -vAge * 0.22 ) );
      float n2 = vnoise( sp * 2.1 - vec2( vAge * 0.5, vAge * 0.3 ) + 7.0 );
      float sheen = n1 * 0.65 + n2 * 0.35;
      // bubbles: noise cells on a clock; each swells, shows a bright cap and an ink ring, then pops
      vec2 bc = floor( p * 7.0 + vP.y ), bf = fract( p * 7.0 + vP.y ) - 0.5;
      float ph = fract( vAge * ( 0.5 + 0.6 * h21( bc ) ) + h21( bc + 3.1 ) );
      float br = 0.1 + 0.28 * ph;
      float bd = length( bf + ( vec2( h21( bc + 1.7 ), h21( bc + 5.3 ) ) - 0.5 ) * 0.3 );
      float live = step( 0.55, h21( bc + 9.2 ) ) * step( ph, 0.85 );
      float bubCap = live * ( 1.0 - smoothstep( br - 0.05, br, bd ) );
      float bubInk = live * smoothstep( br - 0.07, br - 0.03, bd ) * ( 1.0 - smoothstep( br - 0.02, br + 0.01, bd ) );
      float bub = max( t.g * step( 0.5, fract( vAge * 0.7 + 0.3 ) ), t2.g * step( 0.5, fract( vAge * 0.7 ) ) );
      float body = smoothstep( 0.12, 0.3, t.r );
      float edge = smoothstep( 0.1, 0.24, t.r ) * ( 1.0 - smoothstep( 0.4, 0.62, t.r ) );
      a = body * 0.88;
      col = mix( vCol2 * 0.85, vCol.rgb * 0.9, smoothstep( 0.3, 0.9, t.r ) * mix( 0.6, 1.2, sheen ) );
      col = mix( col, vCol2 * 0.55, smoothstep( 0.55, 0.75, n2 ) * 0.5 );
      col = mix( col, vec3( 0.025, 0.04, 0.015 ), max( edge, bubInk * 0.8 ) * 0.95 );
      col = mix( col, vCol.rgb * 1.4 + vec3( 0.07, 0.08, 0.05 ), max( max( bub, t.b ) * 0.8, bubCap * smoothstep( 0.4, 0.8, ph ) ) * ( 1.0 - edge ) );
      a = max( a, edge * smoothstep( 0.05, 0.12, t.r ) );
      emis = 0.05 * ( 1.0 - edge ) * smoothstep( 0.4, 1.0, t.r ) * ( 0.6 + 0.8 * sheen );
      ec = vCol.rgb;
    } else if ( st == 2 ) {
      // lightning scar: charred cracks that flicker with charge
      vec3 t = tile( 14.0, p );
      vec3 sc = tile( 11.0, p * 1.3 );
      float fl = step( 0.55, h11( floor( vAge * 14.0 ) + vP.y ) );
      a = clamp( sc.r * 0.55 + t.g * 0.4, 0.0, 1.0 );
      col = vCol2 * 0.25;
      emis = t.r * ( 0.6 + 1.6 * fl ) + t.g * 0.35 * fl;
      ec = vCol.rgb;
    } else if ( st == 3 ) {
      // frost: a pale crust with crystal facets standing out
      vec3 t = tile( 12.0, p );
      a = clamp( t.r * 0.8 + t.g, 0.0, 1.0 );
      col = mix( vCol2, vCol.rgb, t.r ) * ( t.g > 0.1 ? mix( 0.75, 1.25, t.b ) : 1.0 );
      emis = t.g * 0.25;
      ec = vCol.rgb;
    } else {
      // water: a dark wet sheet with ripple rings running out
      vec3 t = tile( 12.0, p );
      float rr = fract( r * 3.0 - vAge * 1.2 );
      float rip = smoothstep( 0.0, 0.08, rr ) * ( 1.0 - smoothstep( 0.1, 0.22, rr ) ) * ( 1.0 - r );
      a = smoothstep( 0.1, 0.3, t.r ) * 0.6;
      col = mix( vCol2, vCol.rgb, rip );
      a = max( a, rip * 0.8 * step( r, 0.95 ) );
    }
    a *= fade * vCol.a;
    emis *= fade;
    if ( a < 0.01 && emis < 0.01 ) discard;
    col = mix( col, vHazeCol, vHaze * 0.6 );
    gl_FragColor = vec4( col * a + ec * emis * ( 1.0 - vHaze * 0.7 ), a );
    return;
  } else if ( mode == 15 ) {
    // gate vortex: three log-spiral arms flowing inward on absolute time (continuous across the
    // overlapping respawns), ink keylines between the arms, a warm gold rim, a capped hot heart
    if ( r > 1.0 ) discard;
    float env = sin( 3.14159 * vLife ); env *= env;
    float ch = vCol.a;
    float ang = th / TAU * 3.0 + log( r + 0.04 ) * 1.4 + uTime * vP.x;
    float sp = fract( ang );
    float band = smoothstep( 0.0, 0.12, sp ) * ( 1.0 - smoothstep( 0.32, 0.5, sp ) );
    float ink = smoothstep( 0.55, 0.62, sp ) * ( 1.0 - smoothstep( 0.68, 0.75, sp ) );
    float body = smoothstep( 0.08, 0.3, r ) * ( 1.0 - smoothstep( 0.84, 0.9, r ) );
    float rim = smoothstep( 0.86, 0.91, r ) * ( 1.0 - smoothstep( 0.95, 1.0, r ) );
    float heart = 1.0 - smoothstep( 0.0, 0.3, r );
    vec3 light = vCol.rgb * band * body * ( 0.35 + 0.65 * r ) + vCol2 * ( rim * 1.2 + heart * heart * 0.5 );
    float dark = ( ink * body * 0.55 + rim * 0.25 ) * ch;
    light *= ch * ( 1.0 - vHaze * 0.6 );
    gl_FragColor = vec4( light, dark ) * env;
    return;
  } else if ( mode == 13 && vP.x > 0.5 ) {
    // ground light (p0 = 1): reads on bright ground, where additive light can't. A soft dark
    // ring (the ground around the source falls into shadow) with an HDR warm core that blooms.
    float f = ( 1.0 - smoothstep( 0.35, 1.0, vLife ) ) * smoothstep( 0.0, 0.04, vAge ) * vCol.a;
    float ring = smoothstep( 0.3, 0.7, r ) * ( 1.0 - smoothstep( 0.75, 1.0, r ) );
    float core = 1.0 - smoothstep( 0.0, 0.6, r );
    float dark = ( ring * 0.42 + core * 0.25 ) * f;
    vec3 light = mix( vCol2, vCol.rgb, 1.0 - smoothstep( 0.0, 0.35, r ) ) * core * core * f;
    if ( dark < 0.004 && core * f < 0.004 ) discard;
    gl_FragColor = vec4( light * ( 1.0 - vHaze * 0.6 ), dark );
    return;
  } else if ( mode == 13 || mode == 14 ) {
    vec3 t = tile( mode == 13 ? 8.0 : 9.0, p );
    float g = mode == 13 ? t.r : ( vP.x < 0.5 ? t.r : vP.x < 1.5 ? t.g : t.b );
    a = g;
    col = mix( vCol2, vCol.rgb, mode == 13 ? t.g : g * g );
    fade = 1.0 - smoothstep( 0.3, 1.0, vLife );
  } else {
    // glow: hard disc with a hot core
    a = step( r, 1.0 );
    col = mix( vCol2, vCol.rgb, step( r, 0.5 ) );
    fade = 1.0 - smoothstep( 0.5, 1.0, vLife );
  }
  a *= fade * vCol.a;
  if ( a < 0.01 ) discard;
  if ( vAdd > 0.5 ) {
    col *= 1.0 - vHaze * 0.7;
    gl_FragColor = vec4( col * a, 0.0 );
  } else {
    col = mix( col, vHazeCol, vHaze * 0.6 );
    gl_FragColor = vec4( col * a, a );
  }
}
`;

export class Cards {
  readonly mesh: THREE.Mesh;
  readonly ring: Ring;
  readonly uniforms = { uTime: { value: 0 } };
  time = 0;
  constructor(cap: number) {
    this.ring = new Ring(cap, STRIDE);
    const r = this.ring;
    const g = quadGeometry(cap);
    g.setAttribute('aA', r.attr(4, 0));
    g.setAttribute('aB', r.attr(4, 4));
    g.setAttribute('aC', r.attr(4, 8));
    g.setAttribute('aD', r.attr(4, 12));
    g.setAttribute('aE', r.attr(4, 16));
    g.setAttribute('aP', r.attr(4, 20));
    for (let i = 0; i < cap; i++) r.arr[i * STRIDE + 3] = -1e6;
    const m = premulMaterial(new THREE.ShaderMaterial({ vertexShader: VS, fragmentShader: FS, uniforms: { ...hazeUniforms(), ...this.uniforms, tAtlas: { value: fxAtlas() } } }));
    m.side = THREE.DoubleSide;
    m.polygonOffset = true; m.polygonOffsetFactor = -2; m.polygonOffsetUnits = -4;
    this.mesh = new THREE.Mesh(g, m);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 11;
  }

  /** Returns the slot (for markers that are moved or cleared later). */
  spawn(
    mode: number, orient: number, x: number, y: number, z: number, ax: number, ay: number, az: number,
    life: number, delay: number, size0: number, size1: number,
    col: RGB, alpha: number, col2: RGB, flags: number, p0 = 0, p1 = 0, p2 = 0, rot = 0, k = 1,
  ): number {
    // a NaN anywhere in an instance becomes NaN pixels, which the HDR bloom smears into blocks
    if (!(x + y + z + ax + ay + az === x + y + z + ax + ay + az)) return -1;
    const a = this.ring.arr, o = this.ring.alloc();
    a[o] = x; a[o + 1] = y; a[o + 2] = z; a[o + 3] = this.time + delay;
    a[o + 4] = ax; a[o + 5] = ay; a[o + 6] = az; a[o + 7] = life;
    a[o + 8] = size0; a[o + 9] = size1; a[o + 10] = mode; a[o + 11] = orient;
    a[o + 12] = col[0] * k; a[o + 13] = col[1] * k; a[o + 14] = col[2] * k; a[o + 15] = alpha;
    a[o + 16] = col2[0] * k; a[o + 17] = col2[1] * k; a[o + 18] = col2[2] * k; a[o + 19] = flags;
    a[o + 20] = p0; a[o + 21] = p1; a[o + 22] = p2; a[o + 23] = rot;
    return o / STRIDE;
  }
  t0(slot: number) { return this.ring.arr[slot * STRIDE + 3]; }
  move(slot: number, x: number, y: number, z: number) {
    const a = this.ring.arr, o = slot * STRIDE;
    a[o] = x; a[o + 1] = y; a[o + 2] = z; this.ring.touch(slot);
  }
  /** Overwrite a card's colour multiplier/alpha (persistent cards whose strength changes). */
  setAlpha(slot: number, alpha: number) {
    this.ring.arr[slot * STRIDE + 15] = alpha; this.ring.touch(slot);
  }
  /** End a card now (keeps a short fade for markers by shortening life). */
  endAt(slot: number, life: number) {
    this.ring.arr[slot * STRIDE + 7] = life; this.ring.touch(slot);
  }
  update(time: number) {
    this.time = time;
    this.uniforms.uTime.value = time;
    this.ring.flush();
  }
}
